import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react'
import { FieldTypes, urlRegexValidator } from 'utils/formUtils';
import GenericPullCredentialsForm from '../GenericPullCredentialsForm';
import SnykMappingFormV2 from './SnykMappingFormV2';
import { Alert, Button, Form, Radio } from 'antd';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import SnykMappingFormNew from './SnykMappingFormNew';
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import message from 'components/CustomMessage';
import ModalUtil from 'utils/modalUtil';
import SnykOAuthPopup from './SnykOAuthPopup';

function SnykCredentialsForm({config, name, onUpdateConfigComponent, ...props}, ref){
    const [form] = Form.useForm();
    //initially set 'Token Auth' radio checked
    //1 = OAuth Radio value
    //0 = Token Auth Radio value
    //bo_id will always be present, when config is created via OAuth
    const [oAuthVal, setOAuthVal] = useState(() => config ? config.bot_id
    ? 1 : 0 : 0);
    const featureFlags = useFeatureFlag();
    const { sessionData } = useActiveUserContext();
    const thisRef = useRef();

    const MappingForm = featureFlags.SNYK_PROJECTS_BY_TARGET ? SnykMappingFormNew : SnykMappingFormV2;
    const afterSave = props.afterSave;
    const showOAuthPopup = useCallback(() => {
        const autoOnboardProjects = form.getFieldValue("autoOnboardProjects");
        const apiEndpoint = form.getFieldValue("apiEndpoint")
        const ingestSBOM = form.getFieldValue("ingestSBOM")
        ModalUtil.show({
            content: <SnykOAuthPopup {...config} autoOnboardProjects={autoOnboardProjects} apiEndpoint={apiEndpoint} ingestSBOM={ingestSBOM}/>,
            afterClose: (failed, loginId) => {
                if(!failed){
                    if(afterSave){
                        afterSave({id: loginId});
                    }
                    onUpdateConfigComponent && onUpdateConfigComponent(true)
                    message.success(`Snyk OAuth Login Configuration created successfully`);
                    if(!autoOnboardProjects){
                        ModalUtil.show({
                            content: <MappingForm onUpdateConfigComponent={onUpdateConfigComponent} toolName={name} loginConfigId={config?.id ?? loginId}/>
                        })
                    }
                };
                thisRef.current && thisRef.current.close();
            }
        })
    }, [config, name, onUpdateConfigComponent, form, afterSave]);

    const prependFields = useMemo(() => {
        return [{
            type: 'Label',
            value: <>
                <Radio.Group onChange={(e) => {
                    setOAuthVal(e.target.value);
                }}
                defaultValue={oAuthVal}
                >
                    <Radio value={0}>{'Token Auth'}</Radio>
                    {!sessionData.isCustomerDeployedInstance && <Radio value={1}>{'OAuth'}</Radio>}
                </Radio.Group>
            </>,
        }, ...(oAuthVal ? [{
            type: "Label",
            value:  <Button onClick={showOAuthPopup} type='primary'>Connect to Snyk App</Button>
        }] : [])]
    }, [oAuthVal, sessionData.isCustomerDeployedInstance, showOAuthPopup]);

    const fields = [
        {
          name: "apiEndpoint",
          label: "API Endpoint",
          rules: [urlRegexValidator],
          required: true,
          help: "SNYK-US-01 : https://api.snyk.io \n SNYK-US-02 : https://api.us.snyk.io \n SNYK-EU-01 : https://api.eu.snyk.io \n SNYK-AU-01 : https://api.au.snyk.io"
        }
      ];
      
      if (!oAuthVal) {
        fields.push(
          {
            name: "token",
            label: "Token",
            type: FieldTypes.PASSWORD,
            required: true,
          },
          {
            name: "message",
            type: FieldTypes.LABEL,
            value: (
              <Alert
                type="warning"
                message="Projects for this integration will be available after few minutes of adding the credentials, since they are pulled in background."
              />
            ),
          }
        );
      }

      if (featureFlags.MANAGE_SBOM) {
        fields.push({
            name: "ingestSBOM",
            label: "Ingest Components",
            type: FieldTypes.SWITCH,
            help: 'Ingest components and SBOM from the projects mapped under this configuration into the Supply Chain module',
            inline: true,
        });
      }
      

    const getFields = useCallback((fields) =>{
        const filteredFields = oAuthVal ? fields.filter(field => field.name !== "name" && field.name !== "shareable") : fields;
        return filteredFields;
    }, [oAuthVal]);

    const parseExtraFields = useCallback((config) => {
                return {
                    ...config,
                    ingestSBOM : config.ingestSBOM === "true"
                };
            }, [])
    
    return (
        <GenericPullCredentialsForm
            {...props}
            form={form}
            ref={oAuthVal ? thisRef : ref}
            fields={fields}
            prependFields={prependFields}
            MappingForm={MappingForm}
            getFields={getFields}
            name={name}
            onUpdateConfigComponent={onUpdateConfigComponent}
            config={config}
            initialValue={{ apiEndpoint: "https://api.{region}.snyk.io", ingestSBOM: false }}
            parseExtraFields={parseExtraFields}
        />
    )
}

export default forwardRef(SnykCredentialsForm);